<script setup lang="ts">
import { useToastNotification } from '@/composables/useToastNotification';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-vue-next';

const { toasts, removeToast } = useToastNotification();
</script>

<template>
  <div class="fixed bottom-5 right-5 z-50 flex flex-col gap-2.5 max-w-sm w-full pointer-events-none px-4 sm:px-0">
    <TransitionGroup
      enter-active-class="transform ease-out duration-300 transition"
      enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-for="t in toasts"
        :key="t.id"
        class="pointer-events-auto flex items-start gap-3 p-4 rounded-2xl bg-slate-900 border shadow-2xl backdrop-blur-xl"
        :class="[
          t.type === 'success' && 'border-emerald-500/40 text-emerald-300',
          t.type === 'error' && 'border-rose-500/50 bg-slate-900/95 text-rose-300 ring-1 ring-rose-500/30',
          t.type === 'warning' && 'border-amber-500/50 bg-slate-900/95 text-amber-300 ring-1 ring-amber-500/30',
          t.type === 'info' && 'border-sky-500/40 text-sky-300',
        ]"
      >
        <div class="shrink-0 mt-0.5">
          <CheckCircle2 v-if="t.type === 'success'" class="w-5 h-5 text-emerald-400" />
          <AlertCircle v-else-if="t.type === 'error'" class="w-5 h-5 text-rose-400" />
          <AlertTriangle v-else-if="t.type === 'warning'" class="w-5 h-5 text-amber-400" />
          <Info v-else class="w-5 h-5 text-sky-400" />
        </div>

        <div class="flex-1 min-w-0">
          <p class="text-xs font-bold text-slate-100">{{ t.title }}</p>
          <p v-if="t.message" class="mt-0.5 text-xs text-slate-300 leading-relaxed break-words">{{ t.message }}</p>
        </div>

        <button
          type="button"
          @click="removeToast(t.id)"
          class="shrink-0 p-1 text-slate-400 hover:text-slate-200 transition"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>
